import { useCallback, useRef, useState } from "react";

import { SelectData } from "../../models";
import { searchOrganisations, SearchOrganisationsOrganisationType } from "../../service/query";
import { Status } from "../../service/Shared";
import { Toast } from "../../widget";

export type OrganisationDirectoryEntry = {
  name: string;
  type: string;
  subOrganisationUuid: string | null;
};

interface UseOrganisationSearchParams {
  excludeOrganisationUuid?: string;
  excludedProjectUuid?: string;
}

export interface UseOrganisationSearchReturnData {
  organisationOptions: SelectData;
  search: (organisationType: string | null, query: string) => void;
  clear: () => void;
  lookup: (organisationUuid: string) => OrganisationDirectoryEntry | undefined;
}

export const useOrganisationSearch = ({
  excludeOrganisationUuid,
  excludedProjectUuid,
}: UseOrganisationSearchParams): UseOrganisationSearchReturnData => {
  const [organisationOptions, setOrganisationOptions] = useState<SelectData>([]);
  const directory = useRef(new Map<string, OrganisationDirectoryEntry>());
  const searchRequestId = useRef(0);

  const clear = useCallback(() => {
    searchRequestId.current += 1;
    setOrganisationOptions([]);
  }, []);

  const search = useCallback(
    (organisationType: string | null, query: string) => {
      searchRequestId.current += 1;
      const requestId = searchRequestId.current;

      if (!organisationType) {
        setOrganisationOptions([]);
        return;
      }

      searchOrganisations({
        organisationType: organisationType as SearchOrganisationsOrganisationType,
        excludedProjectUuid: excludedProjectUuid || null,
        paging: { limit: 10, beforeCursor: "", afterCursor: "" },
        filter: { results: { displayName: { operator: "startsWith", value: query } } },
      })
        .then((response) => {
          if (searchRequestId.current !== requestId) return;
          if (response.status !== Status.Success || !response.data?.results) {
            setOrganisationOptions([]);
            Toast.error({ message: "Couldn't load organisations. Please try again." });
            return;
          }

          const results = response.data.results
            // BR-02876 - the query exposes the flag and the consumer filters, so an organisation
            // that has opted out of invitations is never offered.
            .filter((organisation) => !organisation.accessInviteOptOut)
            .filter((organisation) => organisation.organisationUuid !== excludeOrganisationUuid);

          results.forEach((organisation) =>
            directory.current.set(organisation.organisationUuid, {
              name: organisation.displayName ?? "",
              type: organisation.organisationType,
              subOrganisationUuid: organisation.subOrganisationUuid,
            })
          );

          setOrganisationOptions(
            results.map((organisation) => ({
              key: organisation.organisationUuid,
              value: organisation.displayName ?? "",
            }))
          );
        })
        .catch(() => {
          if (searchRequestId.current !== requestId) return;
          setOrganisationOptions([]);
          Toast.error({ message: "Couldn't load organisations. Please try again." });
        });
    },
    [excludeOrganisationUuid, excludedProjectUuid]
  );

  const lookup = useCallback((organisationUuid: string) => directory.current.get(organisationUuid), []);

  return { organisationOptions, search, clear, lookup };
};
